import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CustomFieldsManager } from "@/components/v2/CustomFieldsManager";
import { CustomerScopePickers } from "@/components/v2/CustomerScopePickers";
import { CustomerTagsManager } from "@/components/v2/CustomerTagsManager";
import { InventoryCategoryScopePicker } from "@/components/v2/InventoryCategoryScopePicker";
import { Tabs } from "@/components/v2/Tabs";
import { EmptyState } from "@/components/v2/EmptyState";
import { Button } from "@/components/ui/button";
import { ChevronRight, ClipboardList, Sparkles, Tag as TagIcon } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import {
  createCustomerCustomField,
  deleteCustomerCustomField,
  reorderCustomerCustomFields,
  updateCustomerCustomField,
  useCustomerCustomFields,
} from "@/lib/customer-custom-fields";
import {
  createInventoryCustomField,
  deleteInventoryCustomField,
  reorderInventoryCustomFields,
  updateInventoryCustomField,
  useInventoryCustomFields,
} from "@/lib/inventory-custom-fields";

type ScopeKey = "bookings" | "customers" | "inventory";

const SCOPE_TABS: { key: ScopeKey; label: string }[] = [
  { key: "bookings", label: "Bookings" },
  { key: "customers", label: "Customers" },
  { key: "inventory", label: "Inventory" },
];

/**
 * `/workspace/custom-fields` — three tabs for the three custom-field
 * scopes the workspace owns. Booking fields are per-service (this tab
 * is a roster that drills into the per-service editor at
 * `/services/{id}/custom-fields`); customer + inventory fields are
 * tenant-wide and editable in place.
 */
export function WorkspaceCustomFields() {
  const [scope, setScope] = useState<ScopeKey>("bookings");

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Custom fields</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Schema for the data your team captures on bookings, clients, and
          inventory items. Booking fields are configured per-service; client
          and inventory fields apply tenant-wide and can be scoped further by
          tag or category.
        </p>
      </div>

      <Tabs<ScopeKey>
        value={scope}
        onChange={setScope}
        items={SCOPE_TABS}
      />

      {scope === "bookings" && <BookingsScope />}
      {scope === "customers" && <CustomersScope />}
      {scope === "inventory" && <InventoryScope />}
    </div>
  );
}

function BookingsScope() {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);

  const list = useQuery({
    queryKey: ["services"],
    enabled: !!accessToken,
    queryFn: async () => unwrap(await api.GET("/api/v1/services", {})),
  });

  const services = (list.data ?? []) as Array<{
    id: string;
    name: string;
    description?: string;
    active: boolean;
  }>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Booking custom fields are configured per service — each service has its
        own schema. Pick a service to edit its fields.
      </p>

      {list.isLoading && (
        <div className="rounded-lg border bg-card px-4 py-6 text-sm text-muted-foreground">
          Loading services…
        </div>
      )}

      {!list.isLoading && services.length === 0 && (
        <div className="rounded-lg border bg-card">
          <EmptyState
            bare
            icon={ClipboardList}
            title="No services yet"
            description="Create a service first; then come back here to add booking custom fields per service."
            action={
              <Button size="sm" onClick={() => navigate("/services")}>
                Open Services
              </Button>
            }
          />
        </div>
      )}

      {services.length > 0 && (
        <ul className="rounded-lg border bg-card divide-y">
          {services.map((s) => (
            <li
              key={s.id}
              className="px-4 py-3 flex items-center gap-3 hover:bg-muted/30 cursor-pointer"
              onClick={() => navigate(`/services/${s.id}/custom-fields`)}
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{s.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {s.description || "No description"}
                  {!s.active && " · Inactive"}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CustomersScope() {
  const qc = useQueryClient();
  const q = useCustomerCustomFields();
  const fields = q.data?.fields ?? [];
  const [tagsOpen, setTagsOpen] = useState(false);

  const afterChange = () =>
    qc.invalidateQueries({ queryKey: ["customer.custom-fields"] });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setTagsOpen(true)}
        >
          <TagIcon className="h-3.5 w-3.5 mr-1.5" />
          Manage tags
        </Button>
      </div>

      <CustomFieldsManager
        fields={fields}
        copy={{
          description:
            "Captured on every client record. Click a row to configure options + advanced settings, or scope by tag/client.",
          emptyDescription:
            "Add fields like Source, Birthday, or Tax ID to collect structured info on every client.",
          newPlaceholder: "e.g. Source, Birthday, Tax ID",
          deleteDescription:
            "Existing client values for this field are removed. New clients will no longer collect it.",
        }}
        onCreate={(body) => createCustomerCustomField(body)}
        onUpdate={(fieldId, body) => updateCustomerCustomField(fieldId, body)}
        onDelete={(fieldId) => deleteCustomerCustomField(fieldId)}
        onReorder={(ids) => reorderCustomerCustomFields(ids)}
        afterChange={afterChange}
        scopeBody={{
          tabLabel: "Scope",
          helper:
            "Leave both empty to apply to every client. Otherwise the field appears for clients matching ANY of the selected tags or chosen specifically below.",
          render: ({ draft, setDraft }) => (
            <CustomerScopePickers
              tagIds={draft.scopeTagIds ?? []}
              clientIds={draft.scopeClientIds ?? []}
              onTagIdsChange={(next) =>
                setDraft({ ...draft, scopeTagIds: next })
              }
              onClientIdsChange={(next) =>
                setDraft({ ...draft, scopeClientIds: next })
              }
              onManageTags={() => setTagsOpen(true)}
            />
          ),
        }}
      />

      <CustomerTagsManager open={tagsOpen} onClose={() => setTagsOpen(false)} />
    </div>
  );
}

function InventoryScope() {
  const qc = useQueryClient();
  const q = useInventoryCustomFields();
  const fields = q.data?.fields ?? [];

  const afterChange = () =>
    qc.invalidateQueries({ queryKey: ["inventory.custom-fields"] });

  return (
    <CustomFieldsManager
      fields={fields}
      copy={{
        description:
          "Captured on inventory items. Click a row to configure options + advanced settings, or scope to specific categories.",
        emptyDescription:
          "Add fields like Color, Vendor, or Lens type to collect structured info on every inventory item.",
        newPlaceholder: "e.g. Color, Vendor, Lens type",
        deleteDescription:
          "Existing item values for this field are removed. New items will no longer collect it.",
      }}
      onCreate={(body) => createInventoryCustomField(body)}
      onUpdate={(fieldId, body) => updateInventoryCustomField(fieldId, body)}
      onDelete={(fieldId) => deleteInventoryCustomField(fieldId)}
      onReorder={(ids) => reorderInventoryCustomFields(ids)}
      afterChange={afterChange}
      scopeBody={{
        tabLabel: "Scope",
        helper:
          "Leave empty to apply to every item. Otherwise the field appears only on items in the selected categories.",
        render: ({ draft, setDraft }) => (
          <InventoryCategoryScopePicker
            value={draft.scopeCategories ?? []}
            onChange={(next) =>
              setDraft({ ...draft, scopeCategories: next })
            }
          />
        ),
      }}
    />
  );
}
